/**
 * CompositeItem
 *
 * Grouped review item that renders a titled block with one or more rows.
 * Rows can be editable (delegating to `ReviewItem`) or read-only display
 * buttons that still participate in evidence hover/highlight behavior.
 */
import React from 'react';

import { EditableFieldRow } from '@/components/claims-filing/common/EditableFieldRow';
import { cn } from '@/lib/utils';

import { ReviewItem } from './ReviewItem';

/**
 * Row descriptor used by `CompositeItem` to render either an editable
 * `ReviewItem` row or a static display row that still participates in
 * evidence hover/highlight behavior.
 */
interface CompositeRow {
  /** Unique id for the row (used for keys and input ids). */
  id: string;
  /** Display label shown to the left of the value. */
  label: string;
  /** Current value text or input value. */
  value: string;
  /** Render value in monospace, typically for numbers/ids. */
  isMono?: boolean;
  /** If true, renders as an editable `ReviewItem`; otherwise a static row. */
  editable?: boolean;
  /** If true, use InlineEditableRow (hover to edit, update on save). */
  inlineEdit?: boolean;
  /** Whether the value is required to continue. */
  required?: boolean;
  /** Whether this row has been marked as reviewed. */
  isReviewed?: boolean;
  /** Evidence hover key for right pane highlighting. */
  hoverKey?: string;
  /** Change handler for editable rows. */
  onChange?: (next: string) => void;
  /** Accept/mark-reviewed handler for editable rows. */
  onAccept?: () => void;
  /** Save handler for inline edit rows. */
  onSave?: () => void;
  /** Cancel handler for inline edit rows. */
  onCancel?: () => void;
}

/**
 * Props for `CompositeItem`, a grouped review block with a clickable title
 * and one or more child rows (editable or static).
 */
interface CompositeItemProps {
  /** Block title shown in the header. */
  title: string;
  /** Hover key sent when the title is focused/hovered. */
  titleHoverKey?: string;
  /** Rows to render within the block. */
  rows: CompositeRow[];
  /** Optional action area rendered to the right of the title. */
  actions?: React.ReactNode;
  /** Additional class names for the root element. */
  className?: string;
  /** Set the current evidence hover target in the parent. */
  onHover?: (key: string | null) => void;
}

export const CompositeItem: React.FC<CompositeItemProps> = ({
  title,
  titleHoverKey,
  rows,
  actions,
  className,
  onHover,
}) => {
  return (
    <div className={cn('overflow-hidden', className)}>
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-gray-200">
        <button
          type="button"
          className="text-left text-sm font-semibold text-gray-900 flex-1"
          onMouseEnter={() => onHover?.(titleHoverKey || null)}
          onMouseLeave={() => onHover?.(null)}
          onFocus={() => onHover?.(titleHoverKey || null)}
          onBlur={() => onHover?.(null)}
          aria-label={`Highlight evidence for ${title}`}
        >
          {title}
        </button>
        {actions}
      </div>
      {rows.map((row, idx) => {
        let content: React.ReactNode;
        if (row.editable) {
          if (row.inlineEdit) {
            content = (
              <EditableFieldRow
                id={row.id}
                label={row.label}
                value={row.value}
                required={row.required}
                hoverKey={row.hoverKey}
                onHover={onHover}
                onChange={row.onChange}
                onSave={row.onSave}
                onCancel={row.onCancel}
              />
            );
          } else {
            content = (
              <ReviewItem
                id={row.id}
                label={row.label}
                value={row.value}
                required={row.required}
                isReviewed={row.isReviewed}
                hoverKey={row.hoverKey}
                onHover={onHover}
                onChange={row.onChange}
                onAccept={row.onAccept}
              />
            );
          }
        } else {
          content = (
            <button
              type="button"
              className="w-full text-left p-3 text-xs text-gray-700"
              onMouseEnter={() => onHover?.(row.hoverKey || row.id)}
              onMouseLeave={() => onHover?.(null)}
              onFocus={() => onHover?.(row.hoverKey || row.id)}
              onBlur={() => onHover?.(null)}
              aria-label={`Highlight evidence for ${row.label}`}
            >
              <span className="text-gray-600">{row.label}</span>
              <span className="mx-1">•</span>
              <span className={cn('text-gray-900', row.isMono && 'font-mono')}>
                {row.value}
              </span>
            </button>
          );
        }
        return (
          <div key={row.id} className={cn('border-t border-gray-200')}>
            {content}
          </div>
        );
      })}
    </div>
  );
};
